import { FileText, CalendarCheck, ArrowSquareOut, Warning, Compass, CheckCircle, ArrowRight, ChatCircleDots, PauseCircle } from '@phosphor-icons/react';

const ADVISORY_LINK = 'https://www.indielaw.com/searchreportadvisory/';
const COBALT = '#1d57a5';
const NAVY = '#141b4d';
const DECISIONS_EMAIL = 'clients@indielaw.com';

const DECISION_CONFIRMATIONS = {
  PROCEED: "Thanks — we're drafting your application",
  QUESTIONS: "Thanks — we'll be in touch to talk it through",
  PAUSE: "Got it — we'll hold off until you're ready",
};

// Decision row rendered below the report actions. Saves the client's choice and,
// for questions/pause, also opens a pre-filled email so they can add context.
function DecisionRow({ matter, onDecision }) {
  const decided = matter.client_decision;

  const openReply = (suffix, intro) => {
    const subject = `Re: ${matter.trademark_name} search report — ${suffix}`;
    const body = `Hi Indie Law team,\n\n${intro}\n\n`;
    window.location.href = `mailto:${DECISIONS_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (decided) {
    return (
      <div className="mt-4 p-4 flex items-start gap-2" style={{ backgroundColor: '#eef4ec', borderLeft: '3px solid #006747' }} data-testid={`decision-confirmation-${matter.id}`}>
        <CheckCircle weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#006747' }} />
        <p className="text-sm font-semibold" style={{ color: '#101820' }}>
          {DECISION_CONFIRMATIONS[decided] || "Thanks — we've got your decision."}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-5 pt-4" style={{ borderTop: '1px dashed #d4cfc5' }} data-testid={`decision-row-${matter.id}`}>
      <p className="text-sm font-bold mb-3" style={{ color: NAVY }}>What would you like to do?</p>
      <div className="flex flex-col sm:flex-row flex-wrap gap-2.5">
        <button
          onClick={(e) => { e.stopPropagation(); onDecision?.('PROCEED'); }}
          className="inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 transition-transform hover:-translate-y-0.5"
          style={{ backgroundColor: COBALT, color: '#ffffff' }}
          data-testid={`decision-proceed-${matter.id}`}>
          <ArrowRight weight="bold" className="w-4 h-4" /> Proceed — file my application
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); openReply('my questions', `I've reviewed my search report for ${matter.trademark_name} and I have a few questions:`); onDecision?.('QUESTIONS'); }}
          className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 transition-colors hover:bg-[#f2f6fc]"
          style={{ backgroundColor: '#ffffff', color: NAVY, border: `1px solid ${NAVY}` }}
          data-testid={`decision-questions-${matter.id}`}>
          <ChatCircleDots weight="bold" className="w-4 h-4" /> I have questions
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); openReply('pause or pivot', `I'd like to pause or pivot on my ${matter.trademark_name} application. Here's what I'm thinking:`); onDecision?.('PAUSE'); }}
          className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 transition-colors hover:bg-[#f7f3ea]"
          style={{ backgroundColor: '#ffffff', color: '#52525B', border: '1px solid #d4cfc5' }}
          data-testid={`decision-pause-${matter.id}`}>
          <PauseCircle weight="bold" className="w-4 h-4" /> Pause or pivot
        </button>
      </div>
      <p className="text-xs mt-3" style={{ color: '#9a9488' }}>
        Prefer email? Just reply to our message — either works.
      </p>
    </div>
  );
}

export const TIER_CONFIG = {
  'Clear Skies': { risk: 'Very Low', color: '#006747', onColor: '#ffffff', gist: 'No similar marks found.', next: 'Proceed with application.' },
  'Partly Cloudy': { risk: 'Low', color: '#ffd100', onColor: '#141b4d', gist: 'Some similar marks found, but low risk.', next: 'Proceed with application.' },
  'Storm Watch': { risk: 'Moderate', color: '#c2410c', onColor: '#ffffff', gist: 'Potential conflict with another trademark(s).', next: 'Proceed with application but prepare for potential challenges.' },
  'Tornado Warning': { risk: 'High', color: '#b91c1c', onColor: '#ffffff', gist: 'Likely conflict with another trademark(s).', next: 'Consider significant changes to your trademark and/or description before applying — or move forward despite the high risk of refusal.' },
};
const TIER_ORDER = ['Clear Skies', 'Partly Cloudy', 'Storm Watch', 'Tornado Warning'];
const showsAdvisory = (tier) => tier === 'Storm Watch' || tier === 'Tornado Warning';
const isSearchStage = (matter) => ['SEARCH_READY', 'PREP_FILING'].includes(matter?.journey_phase);

// Compact — used on filed/registered matter cards. Renders nothing if no report.
function ReportCompact({ matter, onView }) {
  const cfg = TIER_CONFIG[matter.search_report_tier] || {};
  return (
    <div className="mt-3 pt-3" style={{ borderTop: '1px solid #ece5d8' }} data-testid={`report-inline-${matter.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        {matter.search_report_tier ? (
          <span className="text-xs font-bold px-2 py-1" style={{ backgroundColor: cfg.color, color: cfg.onColor }}>
            {matter.search_report_tier}
          </span>
        ) : <span className="text-xs font-semibold px-2 py-1" style={{ backgroundColor: '#F4F4F5', color: '#52525B' }} data-testid={`report-rating-unavailable-${matter.id}`}>Rating unavailable — see your report</span>}
        {!matter.search_report_viewed_at && (
          <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5" style={{ backgroundColor: '#ffd100', color: '#141b4d' }} data-testid={`report-new-${matter.id}`}>New</span>
        )}
        <a href={matter.search_report_url} target="_blank" rel="noopener noreferrer"
          onClick={(e) => { e.stopPropagation(); onView?.(); }}
          className="report-cta-pop inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2"
          data-testid={`report-link-${matter.id}`}>
          <FileText weight="fill" className="w-4 h-4" /> Read your full search report
        </a>
        {showsAdvisory(matter.search_report_tier) && (
          <a href={ADVISORY_LINK} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 text-xs font-semibold hover:underline"
            style={{ color: COBALT }}>
            <CalendarCheck weight="bold" className="w-3.5 h-3.5" /> Book an advisory call
          </a>
        )}
      </div>
    </div>
  );
}

// Expanded — used on Search Completed matter cards so clients get the full picture
// without having to open View Details.
function ReportExpanded({ matter, onView, isFirstReport, onDecision }) {
  const tier = matter.search_report_tier;
  const cfg = TIER_CONFIG[tier] || {};
  const currentIdx = TIER_ORDER.indexOf(tier);

  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid={`report-expanded-${matter.id}`}>
      {/* Header + tier badge */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h4 className="text-sm font-bold uppercase tracking-wide" style={{ color: '#1d57a5' }}>Your search report is ready</h4>
        {!matter.search_report_viewed_at && (
          <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5" style={{ backgroundColor: '#ffd100', color: '#141b4d' }} data-testid={`report-new-${matter.id}`}>New</span>
        )}
        {tier ? (
          <span className="text-xs font-bold px-2 py-1" style={{ backgroundColor: cfg.color, color: cfg.onColor }}>
            {tier} · {cfg.risk} risk
          </span>
        ) : <span className="text-xs font-semibold px-2 py-1" style={{ backgroundColor: '#F4F4F5', color: '#52525B' }} data-testid={`report-full-rating-unavailable-${matter.id}`}>Rating unavailable — see your report</span>}
      </div>

      {/* Risk spectrum ladder */}
      <div className="flex gap-1.5 mb-2" data-testid={`report-ladder-${matter.id}`}>
        {TIER_ORDER.map((t, i) => {
          const c = TIER_CONFIG[t];
          const active = i === currentIdx;
          return (
            <div key={t} className="flex-1 text-center">
              <div className="h-1.5 w-full mb-1" style={{ backgroundColor: active ? c.color : '#e6ddce' }} />
              <span className="hidden sm:inline text-[10px] font-medium" style={{ color: active ? '#141b4d' : '#9a9488' }}>{t}</span>
            </div>
          );
        })}
      </div>
      {isFirstReport && (
        <p className="text-sm mb-4 leading-relaxed" style={{ color: '#52525B' }} data-testid="weather-explainer">
          We grade every search like a weather forecast — from Clear Skies (very low risk) to Tornado Warning (high risk of a conflict).
        </p>
      )}

      {/* Plain-English gist — two tiles */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="p-4 h-full" style={{ backgroundColor: '#ffffff', border: '1px solid #ece5d8', borderTop: '3px solid #141b4d' }} data-testid={`report-concern-${matter.id}`}>
          <div className="flex items-center gap-1.5 mb-2">
            <Warning weight="fill" className="w-4 h-4" style={{ color: COBALT }} />
            <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>Key concern</span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>{cfg.gist}</p>
        </div>
        <div className="p-4 h-full" style={{ backgroundColor: '#ffffff', border: '1px solid #ece5d8', borderTop: '3px solid #141b4d' }} data-testid={`report-recommendation-${matter.id}`}>
          <div className="flex items-center gap-1.5 mb-2">
            <Compass weight="fill" className="w-4 h-4" style={{ color: '#1d57a5' }} />
            <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>Our recommendation</span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>{cfg.next}</p>
        </div>
      </div>

      {/* Action tiles — Read report + (optionally) Book a call */}
      <div className={`grid gap-3 ${showsAdvisory(tier) ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Read the report tile */}
        <div className="p-4 h-full flex flex-col" style={{ backgroundColor: '#ffffff', border: '1px solid #ece5d8', borderTop: '3px solid #141b4d' }} data-testid={`report-read-tile-${matter.id}`}>
          <div className="flex items-center gap-1.5 mb-2">
            <FileText weight="fill" className="w-4 h-4" style={{ color: '#1d57a5' }} />
            <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>Your search report</span>
          </div>
          <p className="text-sm leading-relaxed mb-4" style={{ color: '#52525B' }}>
            {isFirstReport
              ? "The summary above is just the gist — please read the full report so we're on the same page."
              : 'Open your detailed search report to review the full findings.'}
          </p>
          <a href={matter.search_report_url} target="_blank" rel="noopener noreferrer"
            onClick={(e) => { e.stopPropagation(); onView?.(); }}
            className="report-cta-pop mt-auto inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3"
            data-testid={`report-link-${matter.id}`}>
            <FileText weight="fill" className="w-4 h-4" /> Read your full search report
            <ArrowSquareOut weight="bold" className="w-4 h-4" />
          </a>
        </div>

        {/* Book a call tile — Storm Watch / Tornado Warning only */}
        {showsAdvisory(tier) && (
          <div className="p-4 h-full flex flex-col" style={{ backgroundColor: '#ffffff', border: '1px solid #ece5d8', borderTop: `3px solid ${COBALT}` }} data-testid={`report-advisory-${matter.id}`}>
            <div className="flex items-center gap-1.5 mb-2">
              <CalendarCheck weight="fill" className="w-4 h-4" style={{ color: COBALT }} />
              <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>Talk it through</span>
            </div>
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#52525B' }}>
              Your report shows a real chance of conflict. Book a short advisory call and one of our Trademark Support Coordinators will walk you through your options before you file.
            </p>
            <a href={ADVISORY_LINK} target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="mt-auto inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: COBALT, color: '#ffffff' }}>
              <CalendarCheck weight="bold" className="w-4 h-4" /> Book a Search Report Advisory Call
            </a>
          </div>
        )}
      </div>

      <DecisionRow matter={matter} onDecision={onDecision} />
    </div>
  );
}

// Chooses the compact strip vs. the expanded block based on the matter's stage.
export function SearchReportInline({ matter, onView, isFirstReport, onDecision }) {
  if (!matter?.search_report_url) return null;
  return isSearchStage(matter)
    ? <ReportExpanded matter={matter} onView={onView} isFirstReport={isFirstReport} onDecision={onDecision} />
    : <ReportCompact matter={matter} onView={onView} />;
}

// Full — used on the Matter Detail page.
export function SearchReportFull({ matter, onView }) {
  const hasReport = !!matter?.search_report_url;
  const preFilingSearchStage = ['SEARCH_READY', 'PREP_FILING'].includes(matter?.journey_phase);

  if (!hasReport) {
    if (!preFilingSearchStage) return null;
    return (
      <div className="card-swiss" data-testid="search-report-empty">
        <h2 className="text-lg font-semibold mb-2" style={{ color: '#141b4d' }}>Your search report</h2>
        <p className="text-sm" style={{ color: '#52525B' }}>
          Your search is complete and your report is being finalized. We'll email you the moment it's ready.
        </p>
      </div>
    );
  }

  const tier = matter.search_report_tier;
  const cfg = TIER_CONFIG[tier] || {};
  const currentIdx = TIER_ORDER.indexOf(tier);

  return (
    <div className="card-swiss" data-testid="search-report-full">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h2 className="text-lg font-semibold" style={{ color: '#141b4d' }}>Your search report</h2>
        {matter.search_report_package && (
          <span className="text-xs font-medium px-2 py-0.5" style={{ backgroundColor: '#141b4d', color: '#ffd100' }}>
            {matter.search_report_package}
          </span>
        )}
        {tier ? (
          <span className="text-xs font-bold px-2 py-1" style={{ backgroundColor: cfg.color, color: cfg.onColor }}>
            {tier} · {cfg.risk} risk
          </span>
        ) : <span className="text-xs font-semibold px-2 py-1" style={{ backgroundColor: '#F4F4F5', color: '#52525B' }} data-testid={`report-summary-rating-unavailable-${matter.id}`}>Rating unavailable — see your report</span>}
      </div>

      {/* Risk spectrum ladder */}
      <div className="flex gap-1.5 my-4" data-testid="risk-ladder">
        {TIER_ORDER.map((t, i) => {
          const c = TIER_CONFIG[t];
          const active = i === currentIdx;
          return (
            <div key={t} className="flex-1 text-center">
              <div className="h-2 w-full mb-1.5" style={{ backgroundColor: active ? c.color : '#e6ddce' }} />
              <span className="hidden sm:inline text-[10px] font-medium" style={{ color: active ? '#141b4d' : '#9a9488' }}>{t}</span>
            </div>
          );
        })}
      </div>

      {/* Gist */}
      <div className="p-4 mb-4" style={{ backgroundColor: '#f2e9db' }}>
        <p className="text-sm mb-2" style={{ color: '#101820' }}>
          <span className="font-semibold" style={{ color: '#141b4d' }}>Key concern:</span> {cfg.gist}
        </p>
        <p className="text-sm" style={{ color: '#101820' }}>
          <span className="font-semibold" style={{ color: '#141b4d' }}>Our recommendation:</span> {cfg.next}
        </p>
      </div>

      {/* Primary CTA */}
      <a href={matter.search_report_url} target="_blank" rel="noopener noreferrer"
        onClick={() => onView?.()}
        className="report-cta-pop inline-flex items-center gap-2 text-base font-bold px-6 py-3.5" data-testid="read-full-report-btn">
        <FileText weight="fill" className="w-5 h-5" /> Read your full search report
        <ArrowSquareOut weight="bold" className="w-4 h-4" />
      </a>
      <p className="text-xs mt-2 leading-relaxed" style={{ color: '#9a9488' }}>
        This summary is the gist. It's critical that you read the full report so we can stay on the same page.
      </p>

      {/* Advisory call — Storm Watch / Tornado Warning only */}
      {showsAdvisory(tier) && (
        <div className="mt-4 p-4" style={{ backgroundColor: '#f2f6fc', borderLeft: `3px solid ${COBALT}` }} data-testid="advisory-call-cta">
          <p className="text-sm mb-3 leading-relaxed" style={{ color: '#101820' }}>
            Your report shows some real headwinds. Let's talk it through — book a Search Report Advisory call and we'll walk you through your options.
          </p>
          <a href={ADVISORY_LINK} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2"
            style={{ backgroundColor: COBALT, color: '#ffffff' }} data-testid="advisory-call-btn">
            <CalendarCheck weight="bold" className="w-4 h-4" /> Book a Search Report Advisory Call
          </a>
        </div>
      )}

      {/* Standing disclaimers */}
      <div className="mt-4 space-y-2">
        {[
          'Trademark Search Notice: While we use reliable databases, newer filings could arise that impact your registration.',
          'A third party could apply for a similar trademark before your submission. If this occurs, we will contact you with options.',
        ].map((d, i) => (
          <p key={i} className="flex items-start gap-2 text-xs leading-relaxed" style={{ color: '#9a9488' }}>
            <Warning weight="fill" className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#c2410c' }} />
            <span>{d}</span>
          </p>
        ))}
      </div>
    </div>
  );
}
